import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {Button, Modal, Spinner} from "@tag/tag-components-react-v4";
import {CustomerDTO} from "../types/CustomerDTO";
import DataExportService from "../services/DataExportService";

import styles from './DataExportModal.module.scss';
import { ProjectAccents } from "../../General/config/projectAccents";
import { pollDataExportStatus } from "../../General/services/DataExportStatusPoller";

function DataExportModal(props: { visible: boolean, selectedCustomer: CustomerDTO, handleClose: Function, setAlert: Function, handleCustomerMenuModalClose: Function }) {

    const [loading, setLoading] = useState<string|undefined>('');
    const handleMainDataExport = async () => {
        setLoading('Downloading...');

        try {
            const generateResponse = await DataExportService.generateMainDataExport(props.selectedCustomer.clientId, props.selectedCustomer.environmentName);
            const taskId = generateResponse.taskId;

            const status = await pollDataExportStatus(
                () => DataExportService.getMainDataExportStatus(taskId),
                setLoading);

            if (status.errorMessage) {
                props.setAlert({type: 'error', message: status.errorMessage});
                props.handleClose();
                props.handleCustomerMenuModalClose();
            }
            else if (status.downloadUrl) {
                await startDownload(taskId);
            }
            setLoading(undefined);
        } catch (e) {
            setLoading(undefined);
            props.setAlert({type: 'error', message: 'An error has occured with the data export, please contact HSC Care DevOps'});
            props.handleClose();
            props.handleCustomerMenuModalClose();
        }
    }

    const handleExternalDataExport = async () => {
        setLoading('Downloading...');

        try {
            const generateResponse = await DataExportService.generateExternalDataExport(props.selectedCustomer.clientId, props.selectedCustomer.environmentName);
            const taskId = generateResponse.taskId;

            const status = await pollDataExportStatus(
                () => DataExportService.getExternalDataExportStatus(taskId),
                setLoading);

            if (status.errorMessage) {
                props.setAlert({type: 'error', message: status.errorMessage});
                props.handleClose();
                props.handleCustomerMenuModalClose();
            }
            else if (status.downloadUrl) {
                await startDownload(taskId);
            }
            setLoading(undefined);
        } catch (e) {
            setLoading(undefined);
            props.setAlert({type: 'error', message: 'An error has occured with the data export, please contact HSC Care DevOps'});
            props.handleClose();
            props.handleCustomerMenuModalClose();
        }
    }

    /**
     * Asks the API to sign the link rather than using the one on the status, so that the download is
     * recorded in the privileged action log against whoever triggered it.
     */
    const startDownload = async (taskId: string) => {
        const url = await DataExportService.getDataExportDownloadUrl(taskId);

        const link = document.createElement('a');
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    const renderModalContent = () => {
        let modalContent;
        
        if (loading) {
            modalContent = (
                <>
                    <div className={styles.DataExportModalLoaderContainer}>
                        <Spinner>{loading}</Spinner>
                    </div>
                    <div className={styles.DataExportModalKeepsRunning}>
                        You can close this window &mdash; the export carries on running. Track it and download
                        it from <Link to="/peopleplanner/dataexports">Data Exports</Link>.
                    </div>
                </>
            )
        } else {
            modalContent = (
                <div className={styles.DataExportModalButtonContainer}>
                    <div className={styles.DataExportModalButtonElement}>
                        <Button
                            text={'Main'}
                            onClick={() => handleMainDataExport()}
                        />
                    </div>
                    <div className={styles.DataExportModalButtonElement}>
                        <Button
                            text={'External'}
                            onClick={() => handleExternalDataExport()}
                        />
                    </div>
                </div>
            )
        }

        return modalContent;
    }

    let content;
    content = <>
        <Modal
            visible={props.visible}
            accent={ProjectAccents.PeoplePlanner}
            heading={'Data Export'}
            onCancelButtonClick={() => {
                props.handleClose();
            }}
            onClose={() => props.handleClose()}
            primaryButtonProps={{buttonStyle: {'display': 'none'}}}
        >
            <div>
                {renderModalContent()}
            </div>
        </Modal>
    </>

    return (
        <>
            {content}
        </>
    )

}

export default DataExportModal;
